import { useControlResultRepository } from "@/shared/contexts/repository-context";
import { useHasCompanyPermission } from "@/shared/hooks/use-has-permission";
import { useDialogStore } from "@/shared/stores";
import { alertCalloutPromise } from "@/shared/utils/alert-callout-promise";
import { getGetControlResultListPaginatedQueryKey } from "@/spz-api/control-results/control-results";
import type {
  CompanyUsersResponseDto,
  ControlResultColumnResponseDto,
  GetControlResultListPaginatedResponseDto,
} from "@/spz-api/model";
import { useGetMe } from "@/spz-api/user/user";
import { CompanyPermission, type Transition } from "@spz/shared-domain";
import { Button } from "@spz/ui/components/button";
import { PriorityBadge } from "@spz/ui/components/priority-badge";
import { UserInfo } from "@spz/ui/components/user-info";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { PriorityOption } from "../components/priority-selector";
import { useBulkActionStore } from "../store/bulk-action.store";
import { useBulkSelectionStore } from "../store/bulk-selection.store";
import { useControlResultFiltersStore } from "../store/control-result-filters.store";
import { buildApiFilters } from "../utils/filter-parser/filter-parser";
import {
  type SelectionAnalysis,
  useControlResultsBulkWorkflow,
} from "./use-control-results-bulk-workflow";

interface UseControlResultsBulkSelectionProps {
  controlId: string;
  companyId: string;
  totalFilteredCount: number;
  pageRowIds: string[];
  controlResultColumns: ControlResultColumnResponseDto[];
  controlResults: GetControlResultListPaginatedResponseDto | undefined;
}

interface UseControlResultsBulkSelectionReturn {
  showBanner: boolean;
  showAllSelectedBanner: boolean;
  currentPageCount: number;
  clearSelection: () => void;
  totalSelectedCount: number;
  selectAllAcrossFiltered: () => void;
  handleOwnershipChange: (
    ownerId: string | null,
    user: CompanyUsersResponseDto | undefined,
  ) => void;
  canManageOwnership: boolean;
  isBulkActionInProgress: boolean;
  selectionAnalysis: SelectionAnalysis;
  filteredTransitions: Transition[] | null;
  handleTransitionSubmit: (
    transitionId: string,
    formData: Record<string, unknown> | null,
  ) => Promise<void>;
  canManagePriority: boolean;
  handlePriorityChange: (priority: PriorityOption) => void;
}

export function useControlResultsBulkSelection({
  controlId,
  companyId,
  totalFilteredCount,
  pageRowIds,
  controlResultColumns,
  controlResults,
}: UseControlResultsBulkSelectionProps): UseControlResultsBulkSelectionReturn {
  const { data: user } = useGetMe();
  const { openDialog, closeDialog } = useDialogStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    setSelectionEnabled,
    setTotalRowCount,
    resetSelection,
    getHeaderCheckboxState,
    selectionEnabled,
    selectAllAcrossPages,
    getTotalSelectedCount,
    selectAllAcrossFiltered,
    selectedRowIds,
  } = useBulkSelectionStore();

  const totalSelectedCount = getTotalSelectedCount();
  const columnTypes = Object.fromEntries(
    controlResultColumns.map((col) => [col.columnKey, col.type.toLowerCase()]),
  );
  const { columnFilters } = useControlResultFiltersStore();
  const filtersJson = buildApiFilters(columnFilters, columnTypes);
  const controlResultRepository = useControlResultRepository();
  const { isBulkActionInProgress, setIsBulkActionInProgress } =
    useBulkActionStore();

  const bulkUpdateOwnerMutation = useMutation({
    mutationFn: async (newOwnerId: string) => {
      await controlResultRepository.bulkUpdateOwner({
        controlId,
        newOwnerId,
        ...(selectAllAcrossPages
          ? { filters: filtersJson }
          : { resultIds: Array.from(selectedRowIds) }),
      });
    },
    onSettled: () => {
      setIsBulkActionInProgress(false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetControlResultListPaginatedQueryKey(controlId),
      });
    },
  });
  const bulkChangePriorityMutation = useMutation({
    mutationFn: async (newPriorityId: string) => {
      await controlResultRepository.bulkChangePriority({
        controlId,
        newPriorityId,
        ...(selectAllAcrossPages
          ? { filters: filtersJson }
          : { resultIds: Array.from(selectedRowIds) }),
      });
    },
    onSettled: () => {
      setIsBulkActionInProgress(false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetControlResultListPaginatedQueryKey(controlId),
      });
    },
  });
  const canSkip = useHasCompanyPermission(
    CompanyPermission.ControlResultSkip,
    user,
    companyId,
  );

  const canStart = useHasCompanyPermission(
    CompanyPermission.ControlResultStartInvestigation,
    user,
    companyId,
  );

  const canComplete = useHasCompanyPermission(
    CompanyPermission.ControlResultCompleteInvestigation,
    user,
    companyId,
  );

  const canManageOwnership = useHasCompanyPermission(
    CompanyPermission.ControlResultManageOwnership,
    user,
    companyId,
  );
  const canManagePriority = useHasCompanyPermission(
    CompanyPermission.ControlResultChangePriority,
    user,
    companyId,
  );
  const hasAnyActionPermission = useMemo(
    () => canSkip || canStart || canComplete || canManageOwnership,
    [canSkip, canStart, canComplete, canManageOwnership],
  );

  useEffect(() => {
    setSelectionEnabled(hasAnyActionPermission);
  }, [hasAnyActionPermission, setSelectionEnabled]);

  useEffect(() => {
    setTotalRowCount(totalFilteredCount);
  }, [totalFilteredCount, setTotalRowCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset only on controlId change
  useEffect(() => {
    resetSelection();
  }, [controlId, resetSelection]);

  const hasPageData = pageRowIds.length > 0;

  const headerState = useMemo(
    () => (hasPageData ? getHeaderCheckboxState(pageRowIds) : "unchecked"),
    [hasPageData, getHeaderCheckboxState, pageRowIds],
  );

  const pageAllSelected = headerState === "checked" && hasPageData;
  const showBanner = useMemo(
    () =>
      selectionEnabled &&
      !selectAllAcrossPages &&
      pageAllSelected &&
      totalFilteredCount > pageRowIds.length &&
      totalFilteredCount > totalSelectedCount,
    [
      selectionEnabled,
      selectAllAcrossPages,
      pageAllSelected,
      totalFilteredCount,
      pageRowIds.length,
      totalSelectedCount,
    ],
  );

  const showAllSelectedBanner = useMemo(
    () =>
      selectionEnabled &&
      totalFilteredCount === totalSelectedCount &&
      totalSelectedCount > 0,
    [selectionEnabled, totalFilteredCount, totalSelectedCount],
  );

  const handleOwnershipChange = useCallback(
    (ownerId: string | null, user: CompanyUsersResponseDto | undefined) => {
      const confirmBulkAssignment = async () => {
        closeDialog();
        setIsBulkActionInProgress(true);

        await alertCalloutPromise(
          bulkUpdateOwnerMutation.mutateAsync(ownerId ?? ""),
          {
            loading: {
              title: t("bulk_assign_owner_loading", {
                count: totalSelectedCount,
              }),
            },
            success: {
              title: t("bulk_action_success", {
                count: totalSelectedCount,
              }),
            },
            error: {
              title: t("bulk_action_error"),
            },
          },
        );
      };
      openDialog({
        header: {
          title: t("multiple_assignment_confirmation_title"),
          description: t("change_owner_confirm_description", {
            count: totalSelectedCount,
          }),
          icon: "triangle-alert",
        },
        content: (
          <div className="px-6 pb-6">
            <UserInfo
              firstName={ownerId ? user?.firstName : undefined}
              lastName={ownerId ? user?.lastName : undefined}
              fullName={
                ownerId
                  ? `${user?.firstName} ${user?.lastName}`
                  : t("unassigned")
              }
              avatarUrl={ownerId ? user?.avatarUrl : undefined}
              className="max-w-full"
            />
          </div>
        ),
        footer: {
          actions: (
            <>
              <Button onClick={confirmBulkAssignment}>{t("confirm")}</Button>
            </>
          ),
        },
      });
    },
    [
      totalSelectedCount,
      openDialog,
      closeDialog,
      t,
      bulkUpdateOwnerMutation.mutateAsync,
      setIsBulkActionInProgress,
    ],
  );

  const workflowBulkModule = useControlResultsBulkWorkflow({
    controlResults: controlResults,
    workflowPermissions: {
      canStartInvestigation: canStart,
      canSkipResult: canSkip,
      canCompleteInvestigation: canComplete,
    },
    selectedCount: totalSelectedCount,
    filtersJson,
    controlId,
  });

  const handlePriorityChange = useCallback(
    (priority: PriorityOption) => {
      const confirmBulkAssignment = async () => {
        closeDialog();
        setIsBulkActionInProgress(true);
        await alertCalloutPromise(
          bulkChangePriorityMutation.mutateAsync(priority.value),
          {
            loading: {
              title: t("bulk_change_priority_loading", {
                count: totalSelectedCount,
              }),
            },
            success: {
              title: t("bulk_action_success", {
                count: totalSelectedCount,
              }),
            },
            error: {
              title: t("bulk_action_error"),
            },
          },
        );
      };
      openDialog({
        header: {
          title: t("multiple_results_priority_change_confirmation_title"),
          description: t("change_bulk_priority_confirm_description", {
            count: totalSelectedCount,
          }),
          icon: "triangle-alert",
        },
        content: (
          <div className="pb-4 flex justify-center">
            <PriorityBadge
              id={priority.value}
              variant={priority.variant}
              symbol={priority.symbol}
              showLabel={true}
              label={priority.label}
            />
          </div>
        ),
        footer: {
          actions: (
            <>
              <Button onClick={confirmBulkAssignment}>{t("confirm")}</Button>
            </>
          ),
        },
      });
    },
    [
      totalSelectedCount,
      openDialog,
      closeDialog,
      t,
      setIsBulkActionInProgress,
      bulkChangePriorityMutation.mutateAsync,
    ],
  );
  return {
    showBanner,
    showAllSelectedBanner,
    currentPageCount: pageRowIds.length,
    clearSelection: resetSelection,
    totalSelectedCount,
    selectAllAcrossFiltered,
    canManageOwnership,
    handleOwnershipChange,
    isBulkActionInProgress,
    selectionAnalysis: workflowBulkModule.selectionAnalysis,
    filteredTransitions: workflowBulkModule.filteredTransitions,
    handleTransitionSubmit: workflowBulkModule.handleTransitionSubmit,
    canManagePriority,
    handlePriorityChange,
  };
}
